import type { Locale } from "./i18n";
import { DEFAULT_LOCALE } from "./i18n";

/**
 * Locale-aware `<title>` for every route.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS EXISTS — card RE7QWwV5
 * ─────────────────────────────────────────────────────────────────────────────
 * `/login?lang=en` localised every visible string and left the document title
 * in Hebrew: `html[lang]="en"`, `dir="ltr"`, heading "Sign in", CTA "Sign in" —
 * and the browser tab reading `התחברות · OpsAgent`. Measured identical on
 * desktop 1440×900 and mobile 390×844, and byte-identical across HE and EN, so
 * the string was not varying at all.
 *
 * The cause was structural rather than a missed translation: **every** route's
 * `meta` was a static constant — 14 of them, all Hebrew — so no route could
 * ever follow the locale. Fixing only `/login` would have left thirteen
 * identical bugs behind and made the next one look like a new discovery.
 *
 * ⚠️ The locale is read from the ROOT match, not from the route's own loader.
 * Only three routes (`login`, `forgot-password`, `accept-invite`) resolve a
 * locale themselves; the other eleven have no loader that knows about it. The
 * root loader always runs and always resolves the `?lang` → cookie → default
 * precedence, so it is the one source every route can reach — and using it
 * everywhere means the title agrees with the `<html lang>` attribute that the
 * root layout sets from the same value. Those two agreeing is the actual
 * invariant; the specific words are not.
 */

/** Shape of the root loader's data, narrowed to what a title needs. */
type RootData = { locale?: Locale };

/** Shape of the `matches` entry Remix hands to `meta`. */
type MetaMatch = { id: string; data?: unknown };

export function localeFromMatches(matches: readonly MetaMatch[] | undefined): Locale {
  const root = matches?.find((m) => m.id === "root");
  const data = root?.data as RootData | undefined;
  return data?.locale === "en" ? "en" : DEFAULT_LOCALE;
}

/**
 * Build a `meta` export whose title follows the active locale.
 *
 * Usage:
 *   export const meta = titleMeta("התחברות", "Sign in");
 *
 * The `· OpsAgent` suffix is applied here so a route cannot forget it and so
 * the brand half stays identical in both languages.
 */
export function titleMeta(he: string, en: string) {
  return ({ matches }: { matches?: readonly MetaMatch[] }) => [
    { title: `${localeFromMatches(matches) === "en" ? en : he} · OpsAgent` },
  ];
}
